/**
 * Nutrition Summary Hook
 * Daily and weekly aggregates
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { NutritionDailySummary, NutritionGoal, DailySummaryWithGoal } from '@/lib/nutrition';
import { format, subDays, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';

interface UseNutritionSummaryOptions {
  clientId: string;
  date?: Date;
}

// Get summary for a single day
export function useDailySummary({ clientId, date = new Date() }: UseNutritionSummaryOptions) {
  const dateStr = format(date, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['nutrition-summary', clientId, 'daily', dateStr],
    queryFn: async (): Promise<DailySummaryWithGoal | null> => {
      // Get the daily summary
      const { data: summary, error: summaryError } = await supabase
        .from('nutrition_daily_summaries')
        .select('*')
        .eq('client_id', clientId)
        .eq('summary_date', dateStr)
        .maybeSingle();

      if (summaryError) throw summaryError;

      // Get the goal for this date
      // Order by effective_from DESC, then created_at DESC to get the newest goal
      // when multiple goals have the same effective_from date
      const { data: goal, error: goalError } = await supabase
        .from('nutrition_goals')
        .select('*')
        .eq('client_id', clientId)
        .lte('effective_from', dateStr)
        .or(`effective_until.is.null,effective_until.gte.${dateStr}`)
        .order('effective_from', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (goalError) throw goalError;

      if (!summary) {
        // Return empty summary with goal
        return {
          id: '',
          client_id: clientId,
          summary_date: dateStr,
          total_calories: 0,
          total_protein_g: 0,
          total_carbs_g: 0,
          total_fat_g: 0,
          total_fiber_g: 0,
          total_water_oz: 0,
          meal_count: 0,
          pending_count: 0,
          approved_count: 0,
          updated_at: new Date().toISOString(),
          goal: goal as NutritionGoal | null,
        };
      }

      return {
        ...summary,
        goal: goal as NutritionGoal | null,
      } as DailySummaryWithGoal;
    },
    enabled: !!clientId,
    staleTime: 60000, // 1 minute - summaries don't change frequently
  });
}

// Get summaries for a date range (for charts/trends)
export function useSummaryRange(clientId: string, days: number = 7) {
  const endDate = new Date();
  const startDate = subDays(endDate, days - 1);

  return useQuery({
    queryKey: ['nutrition-summary', clientId, 'range', days],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nutrition_daily_summaries')
        .select('*')
        .eq('client_id', clientId)
        .gte('summary_date', format(startDate, 'yyyy-MM-dd'))
        .lte('summary_date', format(endDate, 'yyyy-MM-dd'))
        .order('summary_date', { ascending: true });

      if (error) throw error;

      // Fill in missing days with zero values
      const summaryMap = new Map(
        (data || []).map((s: NutritionDailySummary) => [s.summary_date, s])
      );

      const allDays = eachDayOfInterval({ start: startDate, end: endDate });

      return allDays.map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const existing = summaryMap.get(dateStr);

        if (existing) return existing;

        return {
          id: '',
          client_id: clientId,
          summary_date: dateStr,
          total_calories: 0,
          total_protein_g: 0,
          total_carbs_g: 0,
          total_fat_g: 0,
          total_fiber_g: 0,
          total_water_oz: 0,
          meal_count: 0,
          pending_count: 0,
          approved_count: 0,
          updated_at: new Date().toISOString(),
        } as NutritionDailySummary;
      });
    },
    enabled: !!clientId,
    staleTime: 60000, // 1 minute - summaries don't change frequently
  });
}

// Get weekly summary
export function useWeeklySummary(clientId: string, weekOf: Date = new Date()) {
  const start = startOfWeek(weekOf, { weekStartsOn: 0 }); // Sunday
  const end = endOfWeek(weekOf, { weekStartsOn: 0 }); // Saturday

  return useQuery({
    queryKey: ['nutrition-summary', clientId, 'weekly', format(start, 'yyyy-MM-dd')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nutrition_daily_summaries')
        .select('*')
        .eq('client_id', clientId)
        .gte('summary_date', format(start, 'yyyy-MM-dd'))
        .lte('summary_date', format(end, 'yyyy-MM-dd'));

      if (error) throw error;

      // Calculate weekly totals and averages
      const summaries = (data || []) as NutritionDailySummary[];
      const daysWithData = summaries.filter((s) => s.meal_count > 0).length;

      const totals = summaries.reduce(
        (acc, s) => ({
          calories: acc.calories + s.total_calories,
          protein_g: acc.protein_g + s.total_protein_g,
          carbs_g: acc.carbs_g + s.total_carbs_g,
          fat_g: acc.fat_g + s.total_fat_g,
          fiber_g: acc.fiber_g + s.total_fiber_g,
          water_oz: acc.water_oz + s.total_water_oz,
          meal_count: acc.meal_count + s.meal_count,
        }),
        { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0, water_oz: 0, meal_count: 0 }
      );

      const averages = daysWithData > 0
        ? {
            calories: Math.round(totals.calories / daysWithData),
            protein_g: Math.round(totals.protein_g / daysWithData),
            carbs_g: Math.round(totals.carbs_g / daysWithData),
            fat_g: Math.round(totals.fat_g / daysWithData),
            fiber_g: Math.round(totals.fiber_g / daysWithData),
            water_oz: Math.round(totals.water_oz / daysWithData),
          }
        : { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0, water_oz: 0 };

      return {
        startDate: start,
        endDate: end,
        dailySummaries: summaries,
        daysWithData,
        totals,
        averages,
      };
    },
    enabled: !!clientId,
    staleTime: 60000, // 1 minute - summaries don't change frequently
  });
}

// Calculate progress percentages
export function calculateProgress(
  current: number,
  target: number | null | undefined
): { percentage: number; status: 'under' | 'on_target' | 'over' } {
  if (!target || target === 0) {
    return { percentage: 0, status: 'under' };
  }

  const percentage = Math.round((current / target) * 100);

  if (percentage < 80) {
    return { percentage, status: 'under' };
  } else if (percentage <= 110) {
    return { percentage, status: 'on_target' };
  } else {
    return { percentage, status: 'over' };
  }
}
